export type InventoryAvailabilityInput = {
  trackInventory?: unknown
  stock?: unknown
  inventoryLevels?: unknown
  variants?: unknown
}

export type InventoryAvailability = {
  tracked: boolean
  quantity: number | null
  inStock: boolean
  label: string
}

const toNonNegativeInteger = (value: unknown): number | null => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  return Math.max(0, Math.floor(numeric))
}

const resolveInventoryLevelStock = (inventoryLevels: unknown): number | null => {
  if (!Array.isArray(inventoryLevels) || inventoryLevels.length === 0) {
    return null
  }

  let total = 0
  let hasValue = false

  inventoryLevels.forEach((entry) => {
    const stock = toNonNegativeInteger((entry as { stock?: unknown })?.stock)
    if (stock === null) return
    total += stock
    hasValue = true
  })

  return hasValue ? total : null
}

const resolveDirectOrLevelStock = (
  stock: unknown,
  inventoryLevels: unknown
): number | null => {
  const levelsStock = resolveInventoryLevelStock(inventoryLevels)
  if (levelsStock !== null) {
    return levelsStock
  }
  return toNonNegativeInteger(stock)
}

const resolveVariantAggregateStock = (
  variants: unknown,
  fallbackTrackInventory: boolean
): { quantity: number | null; hasUntrackedVariant: boolean } => {
  if (!Array.isArray(variants) || variants.length === 0) {
    return { quantity: null, hasUntrackedVariant: false }
  }

  let total = 0
  let hasValue = false
  let hasUntrackedVariant = false

  variants.forEach((variant) => {
    if (!variant || typeof variant !== "object") return
    const record = variant as {
      trackInventory?: unknown
      stock?: unknown
      inventory_quantity?: unknown
      inventoryLevels?: unknown
    }
    const trackInventory =
      typeof record.trackInventory === "boolean"
        ? record.trackInventory
        : fallbackTrackInventory

    if (!trackInventory) {
      hasUntrackedVariant = true
      return
    }

    const quantity = resolveDirectOrLevelStock(
      record.stock ?? record.inventory_quantity,
      record.inventoryLevels
    )
    if (quantity === null) return
    total += quantity
    hasValue = true
  })

  return {
    quantity: hasValue ? total : null,
    hasUntrackedVariant,
  }
}

export const resolveInventoryAvailability = (
  input: InventoryAvailabilityInput
): InventoryAvailability => {
  const tracked = input.trackInventory !== false
  if (!tracked) {
    return {
      tracked: false,
      quantity: null,
      inStock: true,
      label: "Stock disponible",
    }
  }

  const variantAggregate = resolveVariantAggregateStock(input.variants, tracked)
  if (variantAggregate.hasUntrackedVariant) {
    return {
      tracked: false,
      quantity: null,
      inStock: true,
      label: "Stock disponible",
    }
  }

  const quantity =
    variantAggregate.quantity ??
    resolveDirectOrLevelStock(input.stock, input.inventoryLevels) ??
    0
  const inStock = quantity > 0
  const label = inStock
    ? quantity === 1
      ? "1 disponible"
      : `${quantity} disponibles`
    : "Sin stock"

  return {
    tracked: true,
    quantity,
    inStock,
    label,
  }
}
